import { z } from "zod";

export const TIPOS_DE_ELEMENTO = ["recepcao", "espera", "esterilizacao", "banheiro", "corredor", "texto"] as const;
export type TipoDeElemento = (typeof TIPOS_DE_ELEMENTO)[number];

export const ROTULO_DO_ELEMENTO: Record<TipoDeElemento, string> = {
  recepcao: "Recepção",
  espera: "Sala de espera",
  esterilizacao: "Esterilização",
  banheiro: "Banheiro",
  corredor: "Corredor",
  texto: "Texto",
};

/** Retângulo na grade, em células inteiras: canto (x, y) e tamanho (w, h). */
export type Caixa = { x: number; y: number; w: number; h: number };

export const caixasSeCruzam = (a: Caixa, b: Caixa) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

export const cabeNaGrade = (c: Caixa, colunas: number, linhas: number) => c.x >= 0 && c.y >= 0 && c.x + c.w <= colunas && c.y + c.h <= linhas;

const caixa = {
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().min(1),
  h: z.number().int().min(1),
};

export const salaNaPlantaSchema = z.object({ salaId: z.string().min(1), ...caixa });
export const elementoDaPlantaSchema = z.object({
  id: z.string().min(1).max(40),
  tipo: z.enum(TIPOS_DE_ELEMENTO),
  rotulo: z.string().trim().max(40).optional(),
  ...caixa,
});

/** A maquete da clínica: salas e elementos (recepção, corredor...) numa grade, sem sobreposição. */
export const plantaSchema = z
  .object({
    colunas: z.number().int().min(12).max(48),
    linhas: z.number().int().min(8).max(32),
    salas: z.array(salaNaPlantaSchema).max(100),
    elementos: z.array(elementoDaPlantaSchema).max(100),
  })
  .superRefine((p, ctx) => {
    const pecas: Caixa[] = [...p.salas, ...p.elementos];
    if (pecas.some((c) => !cabeNaGrade(c, p.colunas, p.linhas))) ctx.addIssue({ code: "custom", message: "Há peça fora da planta." });
    if (new Set(p.salas.map((s) => s.salaId)).size !== p.salas.length) ctx.addIssue({ code: "custom", message: "Sala repetida na planta." });
    if (new Set(p.elementos.map((e) => e.id)).size !== p.elementos.length) ctx.addIssue({ code: "custom", message: "Elemento repetido na planta." });
    if (pecas.some((a, i) => pecas.some((b, j) => j > i && caixasSeCruzam(a, b)))) ctx.addIssue({ code: "custom", message: "Duas peças ocupam o mesmo lugar." });
  });

export type Planta = z.infer<typeof plantaSchema>;
export type SalaNaPlanta = z.infer<typeof salaNaPlantaSchema>;
export type ElementoDaPlanta = z.infer<typeof elementoDaPlantaSchema>;

export const plantaVazia = (): Planta => ({ colunas: 24, linhas: 16, salas: [], elementos: [] });

/** Primeiro canto livre (linha a linha) onde uma peça w×h cabe sem cruzar as outras. */
export function primeiroEspacoLivre(planta: Pick<Planta, "colunas" | "linhas">, ocupadas: Caixa[], w: number, h: number): Caixa | null {
  for (let y = 0; y + h <= planta.linhas; y++) {
    for (let x = 0; x + w <= planta.colunas; x++) {
      const c = { x, y, w, h };
      if (!ocupadas.some((o) => caixasSeCruzam(o, c))) return c;
    }
  }
  return null;
}

/** Move/redimensiona respeitando a grade e as outras peças: se a nova posição não serve, fica onde está. */
export function encaixar(planta: Pick<Planta, "colunas" | "linhas">, outras: Caixa[], atual: Caixa, proposta: Caixa): Caixa {
  const c = {
    w: Math.max(1, Math.min(proposta.w, planta.colunas)),
    h: Math.max(1, Math.min(proposta.h, planta.linhas)),
    x: Math.round(proposta.x),
    y: Math.round(proposta.y),
  };
  c.x = Math.max(0, Math.min(c.x, planta.colunas - c.w));
  c.y = Math.max(0, Math.min(c.y, planta.linhas - c.h));
  return outras.some((o) => caixasSeCruzam(o, c)) ? atual : c;
}
